import { useState, useEffect, useCallback } from 'react';
import { theme } from '../../lib/theme';

export default function DealRoomPanel({ dealId }) {
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [accessLog, setAccessLog] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('messages');
  const [copied, setCopied] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const token = localStorage?.getItem?.('sb-token') || '';
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchRoom = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`/api/deal-room?action=status&deal_id=${dealId}`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        setRoom(data.room || null);
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [dealId, token]);

  const fetchMessages = useCallback(async () => {
    try {
      const resp = await fetch(`/api/deal-room?action=messages&deal_id=${dealId}`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        setMessages(data.messages || []);
      }
    } catch (e) { /* silent */ }
  }, [dealId, token]);

  const fetchAccessLog = useCallback(async () => {
    try {
      const resp = await fetch(`/api/deal-room?action=access_log&deal_id=${dealId}`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        setAccessLog(data.log || []);
      }
    } catch (e) { /* silent */ }
  }, [dealId, token]);

  useEffect(() => { fetchRoom(); }, [fetchRoom]);
  useEffect(() => {
    if (room?.deal_room_token) {
      if (tab === 'messages') fetchMessages();
      if (tab === 'access') fetchAccessLog();
    }
  }, [room, tab, fetchMessages, fetchAccessLog]);

  // Responsive listener
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleRoom = async () => {
    setEnabling(true);
    const action = room?.deal_room_token ? 'disable' : 'enable';
    try {
      const resp = await fetch('/api/deal-room', {
        method: 'POST', headers,
        body: JSON.stringify({ action, deal_id: dealId }),
      });
      if (resp.ok) await fetchRoom();
    } catch (e) { /* silent */ }
    setEnabling(false);
  };

  const sendMessage = async () => {
    if (!newMsg.trim()) return;
    try {
      await fetch('/api/deal-room', {
        method: 'POST', headers,
        body: JSON.stringify({ action: 'send_message', deal_id: dealId, content: newMsg }),
      });
      setNewMsg('');
      fetchMessages();
    } catch (e) { /* silent */ }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/deal-room/${room.deal_room_token}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  if (loading) {
    return (
      <div style={{
        background: theme.colors.card,
        borderRadius: 12,
        border: `1px solid ${theme.colors.border}`,
        padding: 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 20,
            height: 20,
            borderRadius: 4,
            background: theme.colors.border,
            animation: 'pulse 2s infinite',
          }} />
          <div style={{
            width: 128,
            height: 16,
            borderRadius: 4,
            background: theme.colors.border,
            animation: 'pulse 2s infinite',
          }} />
        </div>
        <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
      </div>
    );
  }

  const isActive = !!room?.deal_room_token;

  return (
    <div style={{
      background: theme.colors.card,
      borderRadius: 12,
      border: `1px solid ${theme.colors.border}`,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '12px 16px' : '16px 20px',
          borderBottom: `1px solid ${theme.colors.border}`,
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: isMobile ? 8 : 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 20 }}>🏠</span>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text, margin: 0 }}>Deal Room</h3>
          {isActive && (
            <span
              style={{
                padding: '3px 10px',
                fontSize: 11,
                fontWeight: 600,
                background: `${theme.colors.success}15`,
                color: theme.colors.success,
                borderRadius: 12,
              }}
            >
              Active
            </span>
          )}
        </div>
        <button
          onClick={toggleRoom}
          disabled={enabling}
          style={{
            padding: isMobile ? '8px 12px' : '10px 16px',
            fontSize: 12,
            fontWeight: 600,
            borderRadius: 8,
            border: isActive ? `1px solid ${theme.colors.danger}` : 'none',
            background: isActive ? 'transparent' : theme.colors.primary,
            color: isActive ? theme.colors.danger : 'white',
            cursor: enabling ? 'default' : 'pointer',
            opacity: enabling ? 0.6 : 1,
            transition: 'all 0.2s',
            minWidth: isMobile ? 'auto' : 120,
            whiteSpace: 'nowrap',
          }}
        >
          {enabling ? '...' : isActive ? 'Disable' : 'Enable'}
        </button>
      </div>

      {!isActive ? (
        <div style={{ padding: isMobile ? '32px 16px' : '48px 32px', textAlign: 'center' }}>
          <div style={{ fontSize: isMobile ? 32 : 48, marginBottom: 12 }}>🔗</div>
          <p style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text, margin: '0 0 8px' }}>
            Share deal details with your client
          </p>
          <p style={{ fontSize: 13, color: theme.colors.textLight, margin: 0 }}>
            Documents, timeline, and messaging in a secure portal
          </p>
        </div>
      ) : (
        <>
          {/* Share link */}
          <div
            style={{
              padding: isMobile ? '12px 16px' : '12px 20px',
              background: `${theme.colors.primary}08`,
              borderBottom: `1px solid ${theme.colors.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: isMobile ? 'wrap' : 'nowrap',
            }}
          >
            <input
              type="text"
              readOnly
              value={`${window.location.origin}/deal-room/${room.deal_room_token}`}
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 12,
                padding: '8px 12px',
                borderRadius: 8,
                border: `1px solid ${theme.colors.border}`,
                background: theme.colors.card,
                color: theme.colors.text,
                fontFamily: 'monospace',
                width: isMobile ? '100%' : 'auto',
              }}
            />
            <button
              onClick={copyLink}
              style={{
                padding: '8px 12px',
                fontSize: 12,
                fontWeight: 600,
                color: theme.colors.primary,
                background: 'transparent',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'background 0.2s',
                width: isMobile ? '100%' : 'auto',
                minHeight: 44,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = `${theme.colors.primary}10`; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>

          {/* Password setting */}
          {room.deal_room_password && (
            <div
              style={{
                padding: '8px 16px',
                background: `${theme.colors.warning}08`,
                borderBottom: `1px solid ${theme.colors.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
              }}
            >
              <span>🔒</span>
              <span style={{ color: theme.colors.warning }}>Password protected</span>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: `1px solid ${theme.colors.border}` }}>
            {[
              { id: 'messages', label: 'Messages', count: messages.length },
              { id: 'access', label: 'Access Log', count: accessLog.length },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  border: 'none',
                  borderBottom: tab === t.id ? `2px solid ${theme.colors.primary}` : 'none',
                  background: 'transparent',
                  color: tab === t.id ? theme.colors.primary : theme.colors.textLight,
                  cursor: 'pointer',
                  minHeight: 44,
                  transition: 'all 0.2s',
                }}
              >
                {t.label} {t.count > 0 && <span style={{ fontSize: 10, marginLeft: 4, opacity: 0.7 }}>({t.count})</span>}
              </button>
            ))}
          </div>

          {/* Messages */}
          {tab === 'messages' && (
            <div>
              <div
                style={{
                  maxHeight: isMobile ? '40vh' : '256px',
                  overflowY: 'auto',
                  padding: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                {messages.length === 0 && (
                  <p style={{ textAlign: 'center', fontSize: 12, color: theme.colors.textLight, padding: '16px 0' }}>
                    No messages yet
                  </p>
                )}
                {messages.map(m => (
                  <div
                    key={m.id}
                    style={{
                      display: 'flex',
                      justifyContent: m.sender_type === 'agent' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <div
                      style={{
                        maxWidth: isMobile ? '85%' : '80%',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        borderRadius: 12,
                        padding: '10px 14px',
                        background: m.sender_type === 'agent' ? theme.colors.primary : theme.colors.bg,
                        color: m.sender_type === 'agent' ? 'white' : theme.colors.text,
                      }}
                    >
                      <p style={{ fontSize: 13, margin: 0, lineHeight: 1.4 }}>{m.content}</p>
                      <p
                        style={{
                          fontSize: 11,
                          margin: '6px 0 0',
                          opacity: 0.7,
                          lineHeight: 1.2,
                        }}
                      >
                        {timeAgo(m.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div
                style={{
                  borderTop: `1px solid ${theme.colors.border}`,
                  padding: 12,
                  display: 'flex',
                  gap: 8,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <input
                  type="text"
                  value={newMsg}
                  onChange={e => setNewMsg(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendMessage()}
                  placeholder="Type a message..."
                  style={{
                    flex: 1,
                    fontSize: 12,
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: `1px solid ${theme.colors.border}`,
                    background: theme.colors.card,
                    color: theme.colors.text,
                    outline: 'none',
                    minHeight: 44,
                  }}
                />
                <button
                  onClick={sendMessage}
                  style={{
                    padding: '10px 16px',
                    background: theme.colors.primary,
                    color: 'white',
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: 8,
                    border: 'none',
                    cursor: 'pointer',
                    minHeight: 44,
                    minWidth: isMobile ? '100%' : 'auto',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = theme.colors.primaryDark; }}
                  onMouseLeave={e => { e.currentTarget.style.background = theme.colors.primary; }}
                >
                  Send
                </button>
              </div>
            </div>
          )}

          {/* Access Log */}
          {tab === 'access' && (
            <div style={{ maxHeight: isMobile ? '40vh' : '256px', overflowY: 'auto' }}>
              {accessLog.length === 0 && (
                <p style={{ textAlign: 'center', fontSize: 12, color: theme.colors.textLight, padding: '32px 16px' }}>
                  No visits yet
                </p>
              )}
              {accessLog.map((entry, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    borderBottom: i < accessLog.length - 1 ? `1px solid ${theme.colors.border}40` : 'none',
                    fontSize: 12,
                    minHeight: 44,
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                  }}
                >
                  <span style={{ fontSize: 16, flexShrink: 0 }}>👁</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: theme.colors.text, fontWeight: 500 }}>
                      {entry.visitor_name || 'Anonymous'}
                    </div>
                    {entry.ip_address && (
                      <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 2 }}>
                        {entry.ip_address}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: 10, color: theme.colors.textLight, flexShrink: 0, textAlign: 'right' }}>
                    {timeAgo(entry.accessed_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
